import type { ActivityEventType } from "@websim/core/src/data/types";
import type { Json } from "@websim/supabase";

import { EventLogger } from "./event-logger";

export function initImpressionTracking(args: {
  distinctId?: string;
  sessionId?: string;
}) {
  const logger = new EventLogger(args);

  const getRankInDom = (element: Element): number => {
    const allElements = Array.from(document.querySelectorAll("[w-pid]"));
    return allElements.indexOf(element) + 1; // 1-based index
  };
  const getRankInFeed = (
    element: Element
  ): { feed_id: string; rank: number } | undefined => {
    // get ancestor with w-fid
    const ancestor = element.closest("[w-fid]");
    if (!ancestor) return undefined;
    const allElements = Array.from(ancestor.querySelectorAll("[w-pid]"));
    return {
      feed_id: ancestor.getAttribute("w-fid") ?? "",
      rank: allElements.indexOf(element) + 1, // 1-based index
    };
  };

  const logForHomepageItem = (target: Element, type: ActivityEventType) => {
    const projectId = target.getAttribute("w-pid");
    const projectVersion = target.getAttribute("w-pver");
    if (projectId) {
      const { feed_id, rank: rank_feed } = getRankInFeed(target) ?? {};
      const attributes: Record<string, Json | undefined> = {
        rank_dom: getRankInDom(target),
        rank_feed,
        feed_id,
        projectVersion,
      };
      logger.logEvent(
        projectId,
        type,
        Object.fromEntries(
          Object.entries(attributes).filter(([_, v]) => v !== undefined)
        ) as Record<string, Json>
      );
    }
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        logForHomepageItem(entry.target, "impression");
        observer.unobserve(entry.target);
      }
    });
  });

  const handleNode = (node: Node): void => {
    if ((node as HTMLElement).hasAttribute("w-pid")) {
      observer.observe(node as HTMLElement);
    }
  };

  const traverseAndHandleNode = (node: Node): void => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      handleNode(node);
      // Traverse the child nodes recursively
      const childNodes = node.childNodes;
      childNodes.forEach((childNode: Node) => {
        traverseAndHandleNode(childNode);
      });
    }
  };

  // Initial check for existing elements
  document.querySelectorAll("[w-pid]").forEach((el) => {
    traverseAndHandleNode(el);
  });

  // Watch for new elements with w-pid
  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === "childList") {
        mutation.addedNodes.forEach((node) => {
          traverseAndHandleNode(node);
        });
      }
    });
  });

  mutationObserver.observe(document, { childList: true, subtree: true });

  // Track when an element is actually clicked. Prevent logging double clicks.
  const clickedProjects: Set<string> = new Set();

  const clickHandler = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const clickedElement = target.closest("[w-pid]") as HTMLElement | null;
    const projectId = clickedElement?.getAttribute("w-pid");
    if (clickedElement && projectId) {
      if (!clickedProjects.has(projectId)) {
        logForHomepageItem(clickedElement, "impression_click");
        clickedProjects.add(projectId);
      }
    }
  };

  document.addEventListener("click", clickHandler, true);

  // Return a cleanup function
  return () => {
    observer.disconnect();
    mutationObserver.disconnect();
    document.removeEventListener("click", clickHandler, true);
  };
}
